import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, JSX } from 'react';
import { Formik } from 'formik';
import { buildEditGroupRows } from './buildEditGroupRows.helper';
import styles from '../Group.module.scss';
import { useTranslation } from 'react-i18next';

const { Box, Button, Table, TextField, Icon } = vp;

type EditGroupProps = {
  group: vp.ContactGroupsLists;
  handlers: {
    onEditGroup: (values: vp.CreateGroupRequest) => void;
  };
};

type FC = FunctionComponent<EditGroupProps>;

const EditGroupForm: FC = (props: EditGroupProps) => {
  const { group, handlers } = props;
  const editGroupRow = buildEditGroupRows();
  const { t } = useTranslation('translations', { keyPrefix: 'Groups.Create.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const defaultValues = {
    GroupName: group.GroupName,
    Description: group.Description,
    Service: group.Service,
    Currency: group.ACTCurrencies_ID === '1' ? 'USD' : 'CAD',
  };

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.onEditGroup}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
      enableReinitialize
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        const editGroupTable: vp.TableProps = {
          name: 'edit-group-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: editGroupRow.map(({ label, require, id, preset }) => ({
            Field: (
              <Box ai="center">
                {label} {getRequiredIcon(require)}
              </Box>
            ),
            Input:
              id === 'Service' ? (
                <div className={styles.upperCase}>
                  <TextField
                    preset={preset}
                    key={id}
                    name={id}
                    variant="standard"
                    value={String(values[id])}
                    disabled
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={errors[id] && touched[id] ? errors[id] : null}
                  />
                </div>
              ) : (
                <TextField
                  preset={preset}
                  key={id}
                  name={id}
                  variant="standard"
                  value={String(values[id])}
                  disabled={id === 'Currency'}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
              ),
          })),
        };
        return (
          <form onSubmit={handleSubmit}>
            <Table {...editGroupTable} />
            <Box mt="md" jc="fe">
              <Button id="edit-group-submit" label={tGlobal('Buttons.save')} type="submit" />
            </Box>
          </form>
        );
      }}
    </Formik>
  );
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
  function createdValidationSchema() {
    return Yup.object().shape({
      GroupName: Yup.string().required().max(50, t('Validation.groupNameLength')),
      Description: Yup.string().required().max(200, t('Validation.descriptionLength')),
    });
  }
};

export default EditGroupForm;
